import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

interface PendingUser {
  id: number;
  username: string;
  email: string;
}

interface ApprovedUser {
  id: number;
  username: string;
  email: string;
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<ApprovedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSearch, setPendingSearch] = useState('');
  const [approvedSearch, setApprovedSearch] = useState('');
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);

  const loadPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const users = await authAPI.getPendingUsers();
      setPendingUsers(users);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to load pending users';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadApproved = async () => {
    try {
      const users = await authAPI.getApprovedUsers();
      setApprovedUsers(users);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to load approved users';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const me = await authAPI.me();
        setCurrentUsername(me.username);
        // Backend enforces admin check; if not admin this will 403.
        await loadPending();
        await loadApproved();
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          'Not authorized to view pending users.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        // Non-admin users should not stay on the admin page
        navigate('/', { replace: true });
      }
    };
    init();
  }, [navigate]);

  const handleApprove = async (username: string) => {
    try {
      await authAPI.approveUser(username);
      setPendingUsers((prev) => prev.filter((u) => u.username !== username));
      // Refresh approved list
      await loadApproved();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to approve user';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleReject = async (username: string) => {
    try {
      await authAPI.rejectUser(username);
      setPendingUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to reject user';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleDeleteUser = async (username: string) => {
    try {
      await authAPI.deleteUser(username);
      setApprovedUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to delete user';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !newPassword) return;
    try {
      await authAPI.resetPassword(resetTarget, newPassword);
      setResetSuccess(`Password reset for ${resetTarget}`);
      setResetTarget(null);
      setNewPassword('');
      setTimeout(() => setResetSuccess(null), 3000);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to reset password';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setResetTarget(null);
      setNewPassword('');
    }
  };

  const pendingTerm = pendingSearch.toLowerCase();
  const filteredPending = pendingUsers.filter(
    (u) =>
      u.username.toLowerCase().includes(pendingTerm) ||
      u.email.toLowerCase().includes(pendingTerm)
  );

  const approvedTerm = approvedSearch.toLowerCase();
  const filteredApproved = approvedUsers.filter(
    (u) =>
      u.username.toLowerCase().includes(approvedTerm) ||
      u.email.toLowerCase().includes(approvedTerm)
  );

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
    >
      <div className="flex-1 max-w-6xl mx-auto w-full py-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg w-full h-full flex flex-col p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img
                src="https://www-new.zippyops.com/wp-content/uploads/sites/9/2026/01/cropped-rectcolour-scaled-1.png"
                alt="ZippyOPS"
                style={{ height: '32px' }}
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">User Management</h1>
                <p className="text-xs text-gray-500">
                  Review and manage users who can access the ZippyOPS AI chat.
                </p>
              </div>
            </div>
          </div>

          {/* Current admin */}
          {currentUsername && (
            <div className="mb-3 text-sm text-gray-700">
              Signed in as <strong>{currentUsername}</strong>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-3 text-sm text-red-700 bg-red-100 rounded-lg p-2">
              {error}
            </div>
          )}

          {/* Success message */}
          {resetSuccess && (
            <div className="mb-3 text-sm text-green-700 bg-green-100 rounded-lg p-2">
              {resetSuccess}
            </div>
          )}

          {/* Main content */}
          <div className="border-t border-gray-200 pt-4 mt-2 flex-1 overflow-y-auto space-y-6">
            {/* Pending users */}
            <div>
              <h2 className="text-sm font-semibold text-gray-800 mb-2">Pending Users</h2>
              <div className="mb-2">
                <input
                  type="text"
                  value={pendingSearch}
                  onChange={(e) => setPendingSearch(e.target.value)}
                  placeholder="Search pending users..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                  <span className="spinner" />
                  <span>Loading pending users...</span>
                </div>
              ) : filteredPending.length === 0 ? (
                <div className="text-sm text-gray-500 mb-4">
                  No pending users. New registrations will appear here for approval.
                </div>
              ) : (
                <table className="w-full text-sm mb-4">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2">Username</th>
                      <th className="py-2">Email</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPending.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100">
                        <td className="py-2">{user.username}</td>
                        <td className="py-2">{user.email}</td>
                        <td className="py-2 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => handleApprove(user.username)}
                              className="btn btn-success"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(user.username)}
                              className="btn btn-danger"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Approved users */}
            <div>
              <h2 className="text-sm font-semibold text-gray-800 mb-2">Approved Users</h2>
              <div className="mb-2">
                <input
                  type="text"
                  value={approvedSearch}
                  onChange={(e) => setApprovedSearch(e.target.value)}
                  placeholder="Search approved users..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              {filteredApproved.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No approved users (other than admin).
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2">Username</th>
                      <th className="py-2">Email</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApproved.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100">
                        <td className="py-2">{user.username}</td>
                        <td className="py-2">{user.email}</td>
                        <td className="py-2 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => { setResetTarget(user.username); setNewPassword(''); }}
                              className="btn btn-primary"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.username)}
                              className="btn btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Reset Password Modal */}
          {resetTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Reset Password</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Set a new password for <strong>{resetTarget}</strong>
                </p>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPassword.length >= 6) handleResetPassword();
                  }}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setResetTarget(null); setNewPassword(''); }}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={newPassword.length < 6}
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;

